/**
 * EntitySightingsList - Displays and manages entity sightings with search/filter
 * Handles sighting list display, pagination, and navigation to detail pages
 * @idempotent @atomic
 */
import { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { baseInputClasses, getSelectClasses } from '../../../styles/inputStyles'
import {
  MapPinIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'

const EntitySightingsList = memo(function EntitySightingsList({
  sightings,
  loading,
  searchInput,
  onSearchChange,
  filter,
  onFilterChange,
  pagination,
  onPageChange
}) {
  const navigate = useNavigate()

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Sighting History</h2>
        <p className="text-sm text-gray-500 mt-1">
          View and manage all sightings for this entity
        </p>
      </div>

      {/* Search and Filter */}
      <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            id="sightings-search"
            name="sightings-search"
            placeholder="Search by location or notes..."
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            className={baseInputClasses}
          />
        </div>
        <div className="sm:w-56">
          <select
            id="sightings-filter"
            name="sightings-filter"
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            className={getSelectClasses()}
          >
            <option value="all">All Sightings</option>
            <option value="needs-curation">Needs Curation</option>
            <option value="pending-moderation">Pending Moderation</option>
          </select>
        </div>
      </div>

      {/* Sightings Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading sightings...</div>
        ) : !sightings || sightings.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No sightings found</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reported By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Confirmations
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sightings.map((sighting) => {
                const needsCuration = sighting.needsCuration || (sighting.pendingConfirmations && sighting.pendingConfirmations > 0)
                const rowClass = needsCuration ? 'bg-amber-50' : ''
                
                return (
                  <tr key={sighting.id} className={`hover:bg-gray-50 ${rowClass}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-start">
                        <MapPinIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {sighting.address || 'Unknown location'}
                          </div>
                          {sighting.notes && (
                            <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                              {sighting.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {sighting.reporterName || 'Anonymous'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {new Date(sighting.createdAt || sighting.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-1 flex-wrap">
                        {sighting.pendingConfirmations > 0 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            Pending
                          </span>
                        )}
                        {sighting.moderationStatus === 'pending' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Moderation
                          </span>
                        )}
                        {sighting.moderationStatus === 'approved' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Approved
                          </span>
                        )}
                        {sighting.moderationStatus === 'rejected' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircleIcon className="h-3 w-3 mr-1" />
                            Rejected
                          </span>
                        )}
                        {!sighting.moderationStatus && !sighting.pendingConfirmations && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Unknown
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {sighting.confirmationCount || sighting.confirmations || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => navigate(`/admin/sightings/${sighting.id}`)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      >
                        <EyeIcon className="h-4 w-4" />
                        View
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing page {pagination.page} of {pagination.totalPages}
            {pagination.total > 0 && ` (${pagination.total} total)`}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

export default EntitySightingsList
